import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Imagedisplay from "./Imagedisplay";
import { apiUrl } from "../../config/apiBase";
import { useAuth } from "../../AuthContext";
import { supabase } from "../../config/supabaseClient";
import Sidebar from "../Sidebar";
import Feedback from "../Feedback";
import Footer from "../../Landing Page/Footer";
import AIAssistant from "../../components/AIAssistant";
import "../../components/AIAssistant.css";
import { FaMicrophone, FaStop, FaPlay, FaTrash, FaPencilAlt } from 'react-icons/fa';
import "./QuestionContent.css";

function QuestionContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refreshTokenIfNeeded } = useAuth();
  const params = new URLSearchParams(location.search);
  const jobId = params.get("job_id");
  const [imageData, setImageData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [attemptedDirectFetch, setAttemptedDirectFetch] = useState(false);
  const [showAnswers, setShowAnswers] = useState(false);
  const [loadingImage, setLoadingImage] = useState(true);
  const [loadingQuestions, setLoadingQuestions] = useState(true);
  const [answers, setAnswers] = useState([]);
  const [userAnswers, setUserAnswers] = useState([]);
  const [loadingAnswers, setLoadingAnswers] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [performanceAnalysis, setPerformanceAnalysis] = useState(null);

  // Audio recording state
  const [isRecording, setIsRecording] = useState({});
  const [audioUrls, setAudioUrls] = useState({});
  const [mediaRecorders, setMediaRecorders] = useState({});
  const [recordingTime, setRecordingTime] = useState({});
  const [isTranscribing, setIsTranscribing] = useState({});
  const [speechRecognitions, setSpeechRecognitions] = useState({});

  // Sketch canvas state
  const [showCanvas, setShowCanvas] = useState({});
  const canvasRefs = useRef({});
  const [isDrawing, setIsDrawing] = useState({});
  const [sketchData, setSketchData] = useState({});

  // Effect to receive image data and trigger question generation
  useEffect(() => {
    const handleImageLoaded = async (event) => {
      if (event.detail && event.detail.imageData) {
        const imageData = event.detail.imageData;
        setImageData(imageData);
        setLoadingImage(false); // Image has loaded

        try {
          setLoadingQuestions(true); // Start loading questions
          const token = await refreshTokenIfNeeded();

          // If we have a jobId (coming from history), use the GET endpoint to fetch existing questions
          if (jobId) {
            console.log('[QuestionContent] Fetching questions for job_id:', jobId);
            const response = await fetch(`${apiUrl('/api/interview-questions/')}?job_id=${jobId}`, {
              method: 'GET',
              headers: {
                ...(token && { Authorization: `Bearer ${token}` })
              }
            });

            const data = await response.json();

            if (!response.ok) {
              console.error('[QuestionContent] ❌ Error fetching existing questions:', {
                status: response.status,
                statusText: response.statusText,
                error: data.error,
                details: data.details
              });
              
              // Set a user-friendly error message
              const errorMessage = data.details 
                ? `Failed to load questions: ${data.details}` 
                : data.error || 'Failed to generate questions. Please try again.';
              
              setError(errorMessage);
              setLoadingQuestions(false);
              return;
            }

            if (data.questions) {
              console.log('[QuestionContent] ✅ Questions loaded successfully, count:', data.questions.length);
              setQuestions(data.questions);
            } else {
              console.warn('[QuestionContent] ⚠️ No questions in response');
            }
            setLoadingQuestions(false);
            return;
          }

          // If no jobId (new question generation), use the generate endpoint
          const response = await fetch(`${apiUrl('/api/interview-questions/generate')}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token && { Authorization: `Bearer ${token}` })
            },
            body: JSON.stringify({
              imageUrl: imageData.image_url,
              imageId: imageData.id,
              jobId: imageData.job_id,
              jobTitle: imageData.job_title,
              prompt: imageData.prompt // Add the scenario prompt
            })
          });

          const data = await response.json();
          
          if (!response.ok) {
            // Error but don't fail - image should still display
            
            // Try to fetch existing questions directly from database with retry
            if (imageData.id) {
              let retryCount = 0;
              const maxRetries = 5;
              const retryDelay = 2000; // 2 seconds
              
              while (retryCount < maxRetries) {
                const { data: questionsData, error: dbError } = await supabase
                  .from('interview_question_sets')
                  .select('questions')
                  .eq('image_id', imageData.id)
                  .single();
                
                if (!dbError && questionsData?.questions && questionsData.questions.length > 0) {
                  setQuestions(questionsData.questions);
                  setLoadingQuestions(false); // Questions loaded
                  return; // Success!
                }
                
                retryCount++;
                if (retryCount < maxRetries) {
                  console.log(`Questions not ready, retrying in ${retryDelay/1000}s... (${retryCount}/${maxRetries})`);
                  await new Promise(resolve => setTimeout(resolve, retryDelay));
                }
              }
            }
            
            // If we get here, we couldn't get questions after all retries
            console.error('Failed to load questions after retries');
            setLoadingQuestions(false); // Stop loading even on error
            return;
          }

          if (data.questions && data.questions.length > 0) {
            setQuestions(data.questions);
          } else if (imageData.id) {
            // Empty questions array - retry from database
            console.log('Empty questions array, retrying from database...');
            let retryCount = 0;
            const maxRetries = 5;
            const retryDelay = 2000;
            
            while (retryCount < maxRetries) {
              await new Promise(resolve => setTimeout(resolve, retryDelay));
              
              const { data: questionsData, error: dbError } = await supabase
                .from('interview_question_sets')
                .select('questions')
                .eq('image_id', imageData.id)
                .single();
              
              if (!dbError && questionsData?.questions && questionsData.questions.length > 0) {
                setQuestions(questionsData.questions);
                setLoadingQuestions(false);
                return;
              }
              
              retryCount++;
              console.log(`Questions not ready, retrying... (${retryCount}/${maxRetries})`);
            }
            
            console.error('Failed to load questions after retries');
          }
          setLoadingQuestions(false); // Questions loaded
        } catch (error) {
          console.error('Error in question flow:', error);
          setLoadingQuestions(false); // Stop loading on error
          // Don't throw - let the image display anyway
        }
      }
    };
    
    window.addEventListener('imageLoaded', handleImageLoaded);
    return () => {
      window.removeEventListener('imageLoaded', handleImageLoaded);
    };
  }, [jobId]);

  // Helper function to get hints based on question type
  const getQuestionHint = (question) => {
    const category = question.category?.toLowerCase() || '';
    const questionText = question.question?.toLowerCase() || '';

    // Analyze question content for specific keywords and provide tailored tips
    
    // Time/Space Complexity questions
    if (questionText.includes('time complexity') || questionText.includes('space complexity') || 
        questionText.includes('big o') || questionText.includes('optimize')) {
      return '💡 Tip: Start by explaining your initial approach, then analyze time and space complexity. Discuss optimization strategies and trade-offs.';
    }

    // Design pattern questions
    if (questionText.includes('design pattern') || questionText.includes('architecture') || 
        questionText.includes('structure')) {
      return '💡 Tip: Explain the pattern\'s purpose, key components, and when to use it. Provide a real-world example from your experience.';
    }

    // Debugging/Troubleshooting questions
    if (questionText.includes('debug') || questionText.includes('troubleshoot') || 
        questionText.includes('error') || questionText.includes('fix') || questionText.includes('issue')) {
      return '💡 Tip: Describe your systematic debugging approach - reproduce the issue, isolate the problem, analyze root cause, implement fix, and verify.';
    }

    // Implementation/Coding questions
    if (questionText.includes('implement') || questionText.includes('write') || 
        questionText.includes('code') || questionText.includes('develop')) {
      return '💡 Tip: Clarify requirements first, outline your approach, consider edge cases, then walk through your implementation step-by-step.';
    }

    // System design questions
    if (questionText.includes('system') || questionText.includes('scale') || 
        questionText.includes('distributed') || questionText.includes('infrastructure')) {
      return '💡 Tip: Discuss scalability, reliability, data consistency, and trade-offs. Consider load balancing, caching, database design, and failure handling.';
    }

    // Performance/Optimization questions
    if (questionText.includes('performance') || questionText.includes('faster') || 
        questionText.includes('improve') || questionText.includes('bottleneck')) {
      return '💡 Tip: Identify performance bottlenecks, explain profiling methods, discuss optimization techniques, and quantify improvements with metrics.';
    }

    // Testing questions
    if (questionText.includes('test') || questionText.includes('quality') || 
        questionText.includes('verify') || questionText.includes('validation')) {
      return '💡 Tip: Cover unit tests, integration tests, edge cases, and error scenarios. Explain your testing strategy and tools used.';
    }

    // API/Integration questions
    if (questionText.includes('api') || questionText.includes('integration') || 
        questionText.includes('endpoint') || questionText.includes('rest')) {
      return '💡 Tip: Discuss API design principles, error handling, authentication, rate limiting, and versioning. Provide concrete examples.';
    }

    // Data structure questions
    if (questionText.includes('data structure') || questionText.includes('array') || 
        questionText.includes('tree') || questionText.includes('graph') || questionText.includes('hash')) {
      return '💡 Tip: Explain why you chose this data structure, its advantages/disadvantages, and operations\' time complexity. Compare with alternatives.';
    }

    // Algorithm questions
    if (questionText.includes('algorithm') || questionText.includes('sort') || 
        questionText.includes('search') || questionText.includes('traverse')) {
      return '💡 Tip: Describe the algorithm clearly, analyze its efficiency, walk through an example, and discuss when it\'s most appropriate to use.';
    }

    // Problem-solving/Approach questions
    if (questionText.includes('approach') || questionText.includes('solve') || 
        questionText.includes('strategy') || questionText.includes('how would you')) {
      return '💡 Tip: Break down the problem into steps, explain your thought process, consider multiple solutions, and justify your chosen approach.';
    }

    // Trade-off/Decision questions
    if (questionText.includes('trade-off') || questionText.includes('compare') || 
        questionText.includes('choose') || questionText.includes('vs') || questionText.includes('versus')) {
      return '💡 Tip: Present both options objectively, discuss pros/cons of each, consider the specific context, and explain your decision criteria.';
    }

    // Experience/Example questions
    if (questionText.includes('experience') || questionText.includes('example') || 
        questionText.includes('tell me about') || questionText.includes('describe a time')) {
      return '💡 Tip: Use the STAR method - describe the Situation, your Task, Actions you took, and measurable Results. Be specific and quantify impact.';
    }

    // Explanation/Concept questions
    if (questionText.includes('explain') || questionText.includes('what is') || 
        questionText.includes('define') || questionText.includes('describe')) {
      return '💡 Tip: Define the concept clearly, explain how it works, provide a practical example, and discuss its applications or use cases.';
    }

    // Why/Reasoning questions
    if (questionText.includes('why') || questionText.includes('reason') || 
        questionText.includes('benefit') || questionText.includes('advantage')) {
      return '💡 Tip: Provide clear reasoning with concrete examples, discuss benefits and potential drawbacks, and relate to real-world scenarios.';
    }

    // Category-based fallbacks if no keyword matches
    if (category.includes('algorithm')) {
      return '💡 Tip: Explain your algorithmic approach clearly, analyze complexity, and walk through with a concrete example.';
    }
    if (category.includes('system') || category.includes('design')) {
      return '💡 Tip: Consider scalability, maintainability, and trade-offs. Discuss components, data flow, and failure scenarios.';
    }
    if (category.includes('coding') || category.includes('implementation')) {
      return '💡 Tip: Start with clarifying questions, outline your approach, handle edge cases, and explain your code clearly.';
    }
    if (category.includes('debug')) {
      return '💡 Tip: Describe a systematic debugging process - reproduce, isolate, analyze, fix, and verify.';
    }

    // Default tip
    return '💡 Tip: Structure your answer clearly, use specific examples from your experience, and explain your thought process step-by-step.';
  };

  // Audio recording functions
  const startRecording = async (questionIndex) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      const chunks = [];

      // Set up speech recognition
      const recognition = new (window.SpeechRecognition || window.webkitSpeechRecognition)();
      recognition.continuous = true;
      recognition.interimResults = true;

      recognition.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map(result => result[0])
          .map(result => result.transcript)
          .join('');
        
        setUserAnswers(prev => {
          const newAnswers = [...prev];
          newAnswers[questionIndex] = transcript;
          return newAnswers;
        });
      };

      recognition.onend = () => {
        // Clear transcribing state when recognition ends
        setIsTranscribing(prev => ({ ...prev, [questionIndex]: false }));
      };

      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsTranscribing(prev => ({ ...prev, [questionIndex]: false }));
      };

      mediaRecorder.ondataavailable = (e) => chunks.push(e.data);
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/ogg; codecs=opus' });
        const url = URL.createObjectURL(blob);
        setAudioUrls(prev => ({ ...prev, [questionIndex]: url }));
      };

      setMediaRecorders(prev => ({ ...prev, [questionIndex]: mediaRecorder }));
      setSpeechRecognitions(prev => ({ ...prev, [questionIndex]: recognition }));
      
      mediaRecorder.start();
      recognition.start();
      setIsRecording(prev => ({ ...prev, [questionIndex]: true }));

      // Start recording timer
      const startTime = Date.now();
      const timerInterval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        setRecordingTime(prev => ({ ...prev, [questionIndex]: elapsed }));
      }, 1000);

      // Store timer interval for cleanup
      setMediaRecorders(prev => ({
        ...prev,
        [`${questionIndex}-timer`]: timerInterval
      }));
    } catch (err) {
      console.error('Error starting recording:', err);
      setError('Failed to start recording: ' + err.message);
    }
  };

  const stopRecording = (questionIndex) => {
    const mediaRecorder = mediaRecorders[questionIndex];
    const recognition = speechRecognitions[questionIndex];
    const timerInterval = mediaRecorders[`${questionIndex}-timer`];

    if (mediaRecorder && mediaRecorder.state === 'recording') {
      mediaRecorder.stop();
      recognition?.stop();
      clearInterval(timerInterval);
      setIsRecording(prev => ({ ...prev, [questionIndex]: false }));
      setIsTranscribing(prev => ({ ...prev, [questionIndex]: true }));

      // Clean up media recorder
      mediaRecorder.stream.getTracks().forEach(track => track.stop());
    }
  };

  const playAudio = (questionIndex) => {
    const audio = new Audio(audioUrls[questionIndex]);
    audio.play();
  };

  const deleteAudio = (questionIndex) => {
    // Clean up audio URL
    if (audioUrls[questionIndex]) {
      URL.revokeObjectURL(audioUrls[questionIndex]);
    }

    // Reset states for this question
    setAudioUrls(prev => {
      const newUrls = { ...prev };
      delete newUrls[questionIndex];
      return newUrls;
    });

    setUserAnswers(prev => {
      const newAnswers = [...prev];
      newAnswers[questionIndex] = '';
      return newAnswers;
    });

    setIsRecording(prev => {
      const newRecording = { ...prev };
      delete newRecording[questionIndex];
      return newRecording;
    });

    setRecordingTime(prev => {
      const newTime = { ...prev };
      delete newTime[questionIndex];
      return newTime;
    });
  };

  // Canvas/Sketch functions
  const toggleCanvas = (questionIndex) => {
    setShowCanvas(prev => ({
      ...prev,
      [questionIndex]: !prev[questionIndex]
    }));
  };

  const initCanvas = (canvas, questionIndex) => {
    if (!canvas) return;
    
    // Store the canvas reference
    canvasRefs.current[questionIndex] = canvas;
    
    // Set canvas width to match its container (same as textarea)
    const container = canvas.parentElement;
    if (container) {
      canvas.width = container.offsetWidth;
    }
    
    const ctx = canvas.getContext('2d');
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#2d3748';
    
    // Load existing sketch if available
    if (sketchData[questionIndex]) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0);
      };
      img.src = sketchData[questionIndex];
    }
  };

  const startDrawing = (e, questionIndex) => {
    const canvas = canvasRefs.current[questionIndex];
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    // Handle both mouse and touch events
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(prev => ({ ...prev, [questionIndex]: true }));
  };

  const draw = (e, questionIndex) => {
    if (!isDrawing[questionIndex]) return;
    
    const canvas = canvasRefs.current[questionIndex];
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    // Handle both mouse and touch events
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = (questionIndex) => {
    setIsDrawing(prev => ({ ...prev, [questionIndex]: false }));
    
    // Save canvas data
    const canvas = canvasRefs.current[questionIndex];
    if (canvas) {
      const dataUrl = canvas.toDataURL();
      setSketchData(prev => ({ ...prev, [questionIndex]: dataUrl }));
    }
  };

  const clearCanvas = (questionIndex) => {
    const canvas = canvasRefs.current[questionIndex];
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    setSketchData(prev => {
      const newData = { ...prev };
      delete newData[questionIndex];
      return newData;
    });
  };

  const handleAnswerChange = (questionIndex, answer) => {
    setUserAnswers(prev => {
      const newAnswers = [...prev];
      newAnswers[questionIndex] = answer;
      return newAnswers;
    });
  };

  const handleSubmitAnswers = async () => {
    try {
      setSubmitting(true);
      setError("");

      const answersToSubmit = questions.map((q, index) => ({
        questionId: q.id,
        answer: userAnswers[index] || '',
        audioUrl: audioUrls[index] || null,
        sketchData: sketchData[index] || null  // Include sketch data
      }));

      const token = await refreshTokenIfNeeded();

      const response = await fetch(`${apiUrl('/api/interview-questions/submit')}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify({
          jobId,
          answers: answersToSubmit
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to submit answers: ${response.statusText}`);
      }

      const data = await response.json();
      setPerformanceAnalysis(data.analysis);
      setSubmitted(true);
      setShowAnswers(true);
    } catch (err) {
      console.error('Error submitting answers:', err);
      setError('Failed to submit answers: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Reset state when jobId changes
  useEffect(() => {
    setImageData(null);
    setQuestions([]);
    setAnswers([]);
    setUserAnswers([]);
    setShowAnswers(false);
    setSubmitted(false);
    setPerformanceAnalysis(null);
    setError("");
    setLoadingImage(true); // Reset to loading state
    setLoadingQuestions(true); // Reset to loading state
  }, [jobId]);

  // Button functions
    const handleGenerateAnswers = async () => {
    try {
      setLoadingAnswers(true);
      setError("");

      // Check if questions exist
      if (!questions || questions.length === 0) {
        throw new Error('No questions available to generate answers for');
      }

      // Since questions already have answers when generated, just use them directly
      const answersFromQuestions = questions.map(q => ({
        id: q.id,
        answer: q.answer || 'Answer not available'
      }));

      // Navigate to the answers page with the necessary data
      navigate('/answers', {
        state: {
          questions,
          answers: answersFromQuestions,
          jobId
        }
      });
    } catch (err) {
      console.error('Error generating answers:', err);
      setError('Failed to load answers: ' + err.message);
    } finally {
      setLoadingAnswers(false);
    }
  };

  const handlePersonalizedAnswer = () => {
    navigate('/personalized-answer', { state: { questions, jobId } });
  };

  return (
    <div className="question-content-container">
      <Sidebar activeItem="questions" />
      <div className="main-content">
        <div className="content-header">
          <h2>Technical Interview Scenario</h2>
          <p>This image represents a technical scenario related to the job posting. Use it to answer the questions below.</p>
        </div>
      
        <div className="question-content-body">
          {/* Side-by-side layout: Image on left, Questions on right */}
          <div className="content-layout">
            <div className="image-section scenario-image">
              <h3 className="section-heading">Technical Scenario</h3>
              {loadingImage && (
                <div className="loading-container">
                  <div className="loading-spinner"></div>
                </div>
              )}
              <div style={{ display: loadingImage ? 'none' : 'block' }}>
                <Imagedisplay jobId={jobId} />
              </div>
            </div>
              
              {/* Right side - Questions */}
              <div className="questions-section">
                <h3 className="section-heading">
                  Technical Interview Questions
                  {submitted && <span className="submitted-badge">✓ Completed</span>}
                </h3>

                {/* Display error message if questions failed to load */}
                {error && !loadingQuestions && (
                  <div className="error-message">
                    <div className="error-icon">⚠️</div>
                    <div className="error-content">
                      <strong>Unable to Load Questions</strong>
                      <p>{error}</p>
                      <div className="error-action">
                        <button 
                          className="refresh-btn"
                          onClick={() => window.location.reload()}
                        >
                          🔄 Refresh Page
                        </button>
                        <span className="error-help">Try refreshing to reload the questions</span>
                      </div>
                    </div>
                  </div>
                )}

                {loadingQuestions ? (
                  <div className="loading-container">
                    <div className="loading-spinner"></div>
                  </div>
                ) : questions.length > 0 ? (
                  <div className="questions-list">
                  {questions.map((question, index) => (
                    <div key={question.id || index} className="question-item">
                      <div className="question-header">
                        <span className="question-number">Q{index + 1}</span>
                        <span className="question-category">{question.category}</span>
                      </div>
                      <div className="question-text">
                        {question.question}
                      </div>
                      {!submitted && (
                        <div className="answer-section">
                          <div className="answer-input-container">
                            <textarea
                              className="answer-input"
                              placeholder="Type your answer here... (Be specific and use examples where possible)"
                              value={userAnswers[index] || ''}
                              onChange={(e) => handleAnswerChange(index, e.target.value)}
                              rows={4}
                            />
                            
                            {/* Sketch Canvas */}
                            {showCanvas[index] && (
                              <div className="sketch-canvas-container">
                                <div className="canvas-header">
                                  <span className="canvas-title">Sketch / Write / Draw Formulas or Diagrams</span>
                                  <button
                                    type="button"
                                    className="canvas-clear-btn"
                                    onClick={() => clearCanvas(index)}
                                    title="Clear canvas"
                                  >
                                    Clear
                                  </button>
                                </div>
                                <canvas
                                  ref={(canvas) => initCanvas(canvas, index)}
                                  className="sketch-canvas"
                                  height={300}
                                  onMouseDown={(e) => startDrawing(e, index)}
                                  onMouseMove={(e) => draw(e, index)}
                                  onMouseUp={() => stopDrawing(index)}
                                  onMouseLeave={() => stopDrawing(index)}
                                  onTouchStart={(e) => startDrawing(e, index)}
                                  onTouchMove={(e) => draw(e, index)}
                                  onTouchEnd={() => stopDrawing(index)}
                                />
                              </div>
                            )}
                            
                            <div className="audio-controls-container">
                              <button
                                type="button"
                                className="sketch-btn"
                                onClick={() => toggleCanvas(index)}
                                title={showCanvas[index] ? "Hide sketch canvas" : "Open sketch canvas"}
                              >
                                <FaPencilAlt /> {showCanvas[index] ? 'Hide' : 'Sketch'}
                                {sketchData[index] && !showCanvas[index] && (
                                  <span className="sketch-indicator" title="Sketch saved">✓</span>
                                )}
                              </button>
                              
                              {!isRecording[index] && !audioUrls[index] && (
                                <button
                                  type="button"
                                  className="audio-btn record-btn"
                                  onClick={() => startRecording(index)}
                                  title="Record voice answer"
                                >
                                  <FaMicrophone /> Record
                                </button>
                              )}

                              {isRecording[index] && (
                                <div className="recording-controls">
                                  <button
                                    type="button"
                                    className="audio-btn stop-btn"
                                    onClick={() => stopRecording(index)}
                                    title="Stop recording"
                                  >
                                    <FaStop /> Stop ({recordingTime[index] || 0}s)
                                  </button>
                                  <span className="recording-pulse">● Recording</span>
                                </div>
                              )}

                              {audioUrls[index] && !isRecording[index] && (
                                <div className="playback-controls">
                                  <button
                                    type="button"
                                    className="audio-btn play-btn"
                                    onClick={() => playAudio(index)}
                                    title="Play recording"
                                  >
                                    <FaPlay /> Play
                                  </button>
                                  <button
                                    type="button"
                                    className="audio-btn delete-btn"
                                    onClick={() => deleteAudio(index)}
                                    title="Delete recording"
                                  >
                                    <FaTrash /> Delete
                                  </button>
                                  {isTranscribing[index] && (
                                    <span className="transcribing">Transcribing...</span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="answer-tips">
                            <small>{getQuestionHint(question)}</small>
                          </div>
                        </div>
                      )}
                      {submitted && userAnswers[index] && (
                        <div className="submitted-answer">
                          <strong>Your Answer:</strong>
                          <p>{userAnswers[index]}</p>
                        </div>
                      )}
                    </div>
                  ))}
                  </div>
                ) : !error && (
                  <div style={{
                    textAlign: 'center',
                    padding: '32px',
                    color: '#666'
                  }}>
                    No questions available yet. Please wait while we generate them.
                  </div>
                )}

                {!submitted && questions.length > 0 && (
                  <div className="submit-section">
                    <button
                      className="submit-answers-btn"
                      onClick={handleSubmitAnswers}
                      disabled={submitting || userAnswers.some((a, i) => i < questions.length && (!a || a.trim().length < 10))}
                    >
                      {submitting ? 'Analyzing Your Performance...' : 'Submit Answers for Performance Analysis'}
                    </button>
                    <p className="submit-note">
                      Complete all answers to receive detailed feedback on your interview performance
                    </p>
                  </div>
                )}
              </div>
            </div>
          
            {/* Performance Analysis Dashboard - appears below the questions after submission */}
            {submitted && performanceAnalysis && (
              <div id="performance-analysis" className="scoring-dashboard">
                <div className="scoring-header">
                  <h3 className="scoring-title">Technical Interview Performance Analysis</h3>
                  <div className="overall-score">
                    <div 
                      className="score-circle"
                      style={{
                        background: `conic-gradient(${
                          performanceAnalysis.overall_score >= 80 ? '#28a745' : 
                          performanceAnalysis.overall_score >= 60 ? '#ffc107' : 
                          '#dc3545'
                        } 0% ${performanceAnalysis.overall_score}%, #e2e8f0 ${performanceAnalysis.overall_score}% 100%)`
                      }}
                    >
                      <span className="score-number">{performanceAnalysis.overall_score}</span>
                      <span className="score-label">Overall Performance</span>
                    </div>
                    <div className="recommendation">
                      <span className="rec-badge">
                        {performanceAnalysis.overall_score >= 80 ? 'Strong Hire' : 
                         performanceAnalysis.overall_score >= 60 ? 'Hire' : 
                         performanceAnalysis.overall_score >= 40 ? 'Consider' :
                         'Needs Improvement'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Dimension Breakdown */}
                {performanceAnalysis.dimensions && performanceAnalysis.dimensions.length > 0 && (
                  <div className="score-breakdown">
                    {performanceAnalysis.dimensions.map((dimension, index) => (
                      <div key={index} className="dimension-item">
                        <div className="dimension-header">
                          <span className="dimension-name">{dimension.name}</span>
                          <span 
                            className="dimension-score"
                            style={{
                              background: dimension.score >= 80 ? '#f0fdf4' :
                                         dimension.score >= 60 ? '#fefce8' :
                                         dimension.score >= 40 ? '#fff7ed' :
                                         '#fef2f2',
                              color: dimension.score >= 80 ? '#166534' :
                                     dimension.score >= 60 ? '#92400e' :
                                     dimension.score >= 40 ? '#9a3412' :
                                     '#991b1b',
                              borderColor: dimension.score >= 80 ? '#bbf7d0' :
                                          dimension.score >= 60 ? '#fde68a' :
                                          dimension.score >= 40 ? '#fed7aa' :
                                          '#fecaca'
                            }}
                          >
                            {dimension.score}/100
                          </span>
                        </div>
                        <div className="score-bar">
                          <div
                            className="score-fill"
                            style={{
                              width: `${dimension.score}%`,
                              background: dimension.score >= 80 ? 'linear-gradient(90deg, #16a34a, #22c55e)' :
                                       dimension.score >= 60 ? 'linear-gradient(90deg, #ca8a04, #eab308)' :
                                       dimension.score >= 40 ? 'linear-gradient(90deg, #ea580c, #f97316)' :
                                       'linear-gradient(90deg, #dc2626, #ef4444)'
                            }}
                          ></div>
                        </div>
                        <div className="dimension-details">
                          {dimension.feedback && (
                            <p className="reasoning">{dimension.feedback}</p>
                          )}
                          {(dimension.strengths || dimension.areas_to_improve) && (
                            <div className="strengths-gaps">
                              {dimension.strengths && dimension.strengths.length > 0 && (
                                <div className="strengths">
                                  <strong>Strengths:</strong>
                                  <ul>
                                    {dimension.strengths.map((strength, idx) => (
                                      <li key={idx}>{strength}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {dimension.areas_to_improve && dimension.areas_to_improve.length > 0 && (
                                <div className="gaps">
                                  <strong>Areas to Improve:</strong>
                                  <ul>
                                    {dimension.areas_to_improve.map((area, idx) => (
                                      <li key={idx}>{area}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          
            {/* Action Buttons positioned below both sections */}
            {submitted && questions.length > 0 && (
              <div className="questions-action-buttons">
                <button
                  className="questions-generate-btn"
                  onClick={handleGenerateAnswers}
                >
                  Generate Answers
                </button>
                <button
                  className="questions-personalized-btn"
                  onClick={handlePersonalizedAnswer}
                >
                 Get Personalized Help via AI Chat
                </button>
              </div>
            )}
          </div>
        
        <Feedback />
      </div>
      
      <div className="dashboard-footer">
        <Footer />
      </div>
      
      {/* AI Assistant - Available when answering questions */}
      {!submitted && questions.length > 0 && (
        <AIAssistant 
          currentQuestion={questions.map((q, i) => `Q${i + 1}: ${q.question}`).join('\n\n')}
          userAnswer={userAnswers.map((a, i) => a ? `A${i + 1}: ${a}` : `A${i + 1}: [Not answered yet]`).join('\n\n')}
        />
      )}
    </div>
  );
}

export default QuestionContent;
